import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, Radio, Globe, Smartphone, HandHeart, TrendingUp, Users, Star, Target, Sparkles, DollarSign, BarChart3, ShoppingBag, HeartHandshake, ChevronLeft, ChevronRight, Layers, Coins, Repeat } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const BRAND = "#0c4a38";
const GOLD = "#d4a017";

function money(n) {
  const v = Number(n || 0);
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${v.toLocaleString()}`;
}

const FUND = [
  { label: "Marketing & Growth", pct: 30, amt: "$150,000", note: "Ads, creators, community campaigns & national brand building" },
  { label: "Seller / Creator acquisition", pct: 20, amt: "$100,000", note: "Onboarding Muslim sellers & creators and helping them succeed" },
  { label: "Product & Engineering", pct: 20, amt: "$100,000", note: "Web + iOS + Android features, live video & new capabilities" },
  { label: "Operations", pct: 10, amt: "$50,000", note: "Support, seller success & content moderation" },
  { label: "Infrastructure", pct: 8, amt: "$40,000", note: "Streaming, hosting, storage & scaling for growth" },
  { label: "Legal / Compliance", pct: 5, amt: "$25,000", note: "Entity, contracts, payments & regulatory compliance" },
  { label: "Masjid / Fundraising partnerships", pct: 3, amt: "$15,000", note: "Partnering with masjids & Islamic organizations for LIVE fundraising" },
  { label: "Reserve", pct: 4, amt: "$20,000", note: "Runway buffer & contingency" },
];

const RAISE_GOALS = [
  "Scale from an existing built product into a national marketplace",
  "Acquire Muslim sellers and creators",
  "Partner with masjids and Islamic organizations",
  "Launch and scale LIVE fundraising",
  "Expand Facebook, YouTube and Twitch integrations",
  "Integrate Shopify and WooCommerce",
  "Build the technology and infrastructure needed for scale",
  "Reach our first 100,000 users",
];

const PHASES = [
  { name: "Phase 1", range: "$0–$100K", title: "Launch & validate",
    targets: ["100+ sellers", "50+ creators", "25+ masjids", "5,000+ registered users", "First $100K marketplace GMV", "First LIVE fundraising campaigns"] },
  { name: "Phase 2", range: "$100K–$300K", title: "Scale the marketplace",
    targets: ["500+ sellers", "200+ creators", "100+ masjids", "25,000+ users", "$500K+ cumulative GMV", "Repeat buyers", "Repeat LIVE sellers", "Recurring fundraising campaigns"] },
  { name: "Phase 3", range: "$300K–$500K", title: "National expansion",
    targets: ["1,000+ sellers", "500+ creators", "250+ masjids", "100,000+ users", "$1M+ annualized GMV", "Meaningful recurring revenue"] },
];

// Membership-based monetization roadmap — revenue is layered in as the community grows.
const MONETIZATION = [
  { name: "Phase 1", range: "0–1,500 Members", title: "Build community", tag: "Foundation",
    items: ["Build the founding community", "No disruption to Founding Member benefits"] },
  { name: "Phase 2", range: "1,500–5,000", title: "Introduce", tag: "Monetize",
    items: ["Seller fees", "Seller subscriptions", "Creator commerce", "Featured listings", "Masjid Pro"] },
  { name: "Phase 3", range: "5,000–25,000", title: "Expand", tag: "Grow",
    items: ["Advertising", "Brand sponsorships", "Premium LIVE tools", "Shopify / WooCommerce tools", "Creator partnerships"] },
  { name: "Phase 4", range: "25,000–100,000+", title: "Scale", tag: "Scale",
    items: ["National creator network", "Major Muslim brands", "Large organizations", "Advanced advertising", "Enterprise accounts", "International expansion"] },
];

// Illustrative revenue model at scale (10% effective take rate + other revenue).
const REVENUE = [
  { users: "5,000 users", sellers: "250 active sellers", gmv: "$250K monthly GMV",
    take: "$25K/mo", other: "~$10K/mo", total: "~$35K/mo" },
  { users: "25,000 users", sellers: "1,000 active sellers", gmv: "$750K monthly GMV",
    take: "$75K/mo", other: "~$25K/mo", total: "~$100K/mo", highlight: true },
  { users: "100,000 users", sellers: "4,000 active sellers", gmv: "$3M monthly GMV",
    take: "$300K/mo", other: "~$100–200K/mo", total: "$400–500K/mo" },
];

// The ecosystem flywheel — the founding community compounds into GMV & revenue.
const FLYWHEEL = [
  "1,500 Founding Members", "Creators", "Sellers", "LIVE Commerce",
  "Masjids & Organizations", "Fundraising", "More Buyers", "More GMV", "SouqStream Revenue",
];

function Slide({ children, n, total, cur }) {
  const active = cur === n - 1;
  return (
    <section className={`pitch-slide${active ? "" : " is-hidden"}`} data-testid={`pitch-slide-${n}`} data-active={active ? "true" : "false"}>
      <div style={{ flex: 1 }}>{children}</div>
      <div className="pitch-foot"><span>Souq Stream · Confidential</span><span>{n} / {total}</span></div>
    </section>
  );
}

function Stat({ big, label }) {
  return (
    <div className="ss-card" style={{ padding: "20px 18px", textAlign: "center" }}>
      <div style={{ fontSize: 30, fontWeight: 800, color: "var(--brand)" }}>{big}</div>
      <div className="ss-muted" style={{ fontSize: 13, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function KpiCard({ icon, value, label }) {
  return (
    <div className="ss-card" style={{ padding: "16px 16px", display: "flex", flexDirection: "column", gap: 6 }} data-testid={`traction-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 10, background: "var(--gold-soft)", color: "var(--brand)" }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "var(--brand)", lineHeight: 1.1 }}>{value}</div>
      <div className="ss-muted" style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
}

function TractionCharts({ series }) {
  return (
    <div className="ss-grid grid-2" style={{ marginTop: 14 }} data-testid="traction-charts">
      <div className="ss-card" style={{ padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Member growth</div>
        <div style={{ width: "100%", height: 150 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 4, right: 8, left: -6, bottom: 0 }}>
              <defs>
                <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={BRAND} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
              <Tooltip cursor={{ stroke: BRAND, strokeOpacity: 0.15 }} />
              <Area type="monotone" dataKey="users" name="Members" stroke={BRAND} strokeWidth={2.5} fill="url(#gUsers)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="ss-card" style={{ padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Monthly GMV</div>
        <div style={{ width: "100%", height: 150 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 4, right: 8, left: -2, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={48} tickFormatter={money} />
              <Tooltip cursor={{ fill: "rgba(212,160,23,.08)" }} formatter={(v) => money(v)} />
              <Bar dataKey="gmv" name="GMV" fill={GOLD} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default function Pitch() {
  const navigate = useNavigate();
  const [tr, setTr] = useState(null);
  const [idx, setIdx] = useState(0);
  const T = 13;

  useEffect(() => {
    api.get("/stats/traction").then(setTr).catch(() => setTr(null));
  }, []);

  const go = (i) => setIdx((c) => Math.min(T - 1, Math.max(0, typeof i === "function" ? i(c) : i)));
  const next = () => go((c) => c + 1);
  const prev = () => go((c) => c - 1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Home") { e.preventDefault(); go(0); }
      else if (e.key === "End") { e.preventDefault(); go(T - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const fmtDate = tr?.generated_at
    ? new Date(tr.generated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <div className="pitch-wrap">
      <div className="pitch-toolbar no-print">
        <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => navigate("/about")} data-testid="pitch-back"><ArrowLeft size={15} /> Back</button>
        <div style={{ fontWeight: 800 }}>Investor Pitch Deck</div>
        <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => window.print()} data-testid="pitch-download"><Printer size={15} /> Download / Print PDF</button>
      </div>

      <div className="pitch-stage">
        <button className="pitch-arrow no-print" onClick={prev} disabled={idx === 0} aria-label="Previous slide" data-testid="pitch-prev"><ChevronLeft size={22} /></button>

        <div className="pitch-deck" data-testid="pitch-deck">
        {/* 1 — Cover */}
        <Slide n={1} total={T} cur={idx}>
          <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", background: "var(--brand)", color: "#fff", borderRadius: 18, padding: 40 }}>
            <Logo height={80} />
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.14)", borderRadius: 999, padding: "6px 16px", fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginTop: 22 }}>
              <Sparkles size={14} style={{ color: "var(--gold)" }} /> Investor Pitch
            </div>
            <h1 style={{ fontSize: 40, fontWeight: 800, marginTop: 20, lineHeight: 1.15 }}>The live marketplace<br />for the Muslim community</h1>
            <p style={{ marginTop: 16, fontSize: 17, opacity: .9, maxWidth: 560 }}>Where live shopping, community &amp; giving come together — on web, iOS and Android.</p>
          </div>
        </Slide>

        {/* 2 — Opportunity */}
        <Slide n={2} total={T} cur={idx}>
          <h2 className="pitch-h"><TrendingUp size={22} /> A large, fast-growing, underserved market</h2>
          <div className="ss-grid grid-3" style={{ marginTop: 18 }}>
            <Stat big="~3.45M+" label="Muslims in the U.S. (Pew) — ~1% of adults and the fastest-growing U.S. faith" />
            <Stat big="$170–200B" label="Estimated annual American Muslim consumer spending power" />
            <Stat big="~8.1M" label="Projected U.S. Muslim population by 2050 (Pew)" />
          </div>
          <p style={{ marginTop: 20, fontSize: 15, lineHeight: 1.7, color: "var(--muted)" }}>
            American Muslims are young, digitally native, community-driven and spend across food, fashion, gifting and
            charity — yet they are largely overlooked by mainstream commerce platforms. Live commerce is one of the
            fastest-growing retail formats worldwide, and no one is bringing it to this community with cultural alignment.
          </p>
          <p className="ss-muted" style={{ fontSize: 11, marginTop: 12 }}>Sources: Pew Research Center; DinarStandard / industry estimates.</p>
        </Slide>

        {/* 3 — Problem */}
        <Slide n={3} total={T} cur={idx}>
          <h2 className="pitch-h"><Target size={22} /> The problem</h2>
          <ul className="pitch-list">
            <li><b>No dedicated home.</b> Muslim sellers and shoppers have no mainstream live-shopping platform built for them.</li>
            <li><b>Trust &amp; alignment gap.</b> Buyers want culturally-aligned, values-driven marketplaces they can trust.</li>
            <li><b>Sellers lack reach.</b> Small Muslim-owned businesses struggle to reach their community and sell live.</li>
            <li><b>Giving is disconnected.</b> Fundraising for Islamic causes lives separately from where the community already shops and gathers.</li>
          </ul>
        </Slide>

        {/* 4 — Solution / Product */}
        <Slide n={4} total={T} cur={idx}>
          <h2 className="pitch-h"><Radio size={22} /> The solution — Souq Stream</h2>
          <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--muted)", marginTop: 6 }}>
            A full live-commerce platform where sellers broadcast live and buyers discover, bid, chat and check out in real time.
          </p>
          <div className="ss-grid grid-2" style={{ marginTop: 16 }}>
            {[
              ["Live shows & real-time bidding", "Auctions, buy-it-now, giveaways and live video with LiveKit"],
              ["Multi-platform simulcast", "Sellers go live once and restream to YouTube, Facebook & Twitch — SouqStream is the restreamer"],
              ["Seller tools", "Dashboards, inventory, orders, shipping labels & payouts"],
              ["Real checkout & shipping", "Stripe payments, combined carts, tracking & delivery emails"],
              ["Community & safety", "Live chat, following, reporting, blocking & moderation"],
              ["Store sync & bulk import", "Sellers connect Shopify or WooCommerce, or import via CSV / Google Sheets — prices & inventory stay in sync automatically"],
            ].map(([t, b]) => (
              <div className="ss-card" style={{ padding: 16 }} key={t}>
                <div style={{ fontWeight: 800 }}>{t}</div>
                <div className="ss-muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{b}</div>
              </div>
            ))}
          </div>
          <div className="ss-card" style={{ padding: 16, marginTop: 14, display: "flex", gap: 16, alignItems: "center", background: "var(--surface-3)" }}>
            <Globe size={22} style={{ color: "var(--brand)" }} />
            <Smartphone size={22} style={{ color: "var(--brand)" }} />
            <div style={{ fontWeight: 700, fontSize: 15 }}>Already live in production on <b>Web</b>, <b>iOS</b> and <b>Android</b>.</div>
          </div>
        </Slide>

        {/* 5 — Why we win */}
        <Slide n={5} total={T} cur={idx}>
          <h2 className="pitch-h"><Star size={22} /> Why Souq Stream wins</h2>
          <div className="ss-card" style={{ padding: 22, marginTop: 8, background: "var(--brand)", color: "#fff" }}>
            <div style={{ fontSize: 20, fontWeight: 800 }}>The only live-shopping platform built specifically for the Muslim marketplace in this style.</div>
          </div>
          <ul className="pitch-list">
            <li><b>First-mover</b> in Muslim-focused live commerce across web + mobile.</li>
            <li><b>Islamic Fundraising built in</b> — communities raise funds for causes right where they already gather and shop.</li>
            <li><b>Community & trust moat</b> — culturally aligned experience that mainstream platforms can't easily replicate.</li>
            <li><b>Full stack shipped</b> — payments, shipping, video and moderation already integrated.</li>
          </ul>
        </Slide>

        {/* 6 — Traction */}
        <Slide n={6} total={T} cur={idx}>
          <h2 className="pitch-h"><Users size={22} /> Traction &amp; status</h2>
          <div className="ss-grid grid-3" style={{ marginTop: 16 }}>
            <Stat big="3 apps" label="Web, iOS & Android — all live in production" />
            <Stat big="Invite-only" label="Founding-member launch driving quality & demand" />
            <Stat big="End-to-end" label="Real Stripe payments, shipping labels & emails live" />
          </div>
          <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.7, color: "var(--muted)" }}>
            The product is built and operating: live video shows, auctions, giveaways, fundraisers, seller dashboards,
            checkout and moderation are all working today. We are ready to scale demand.
          </p>
        </Slide>

        {/* 7 — Live traction (real numbers + charts) */}
        <Slide n={7} total={T} cur={idx}>
          <h2 className="pitch-h"><BarChart3 size={22} /> Live traction — real platform numbers</h2>
          <p className="ss-muted" style={{ fontSize: 13, marginTop: 2 }}>
            These figures are pulled live from the SouqStream platform and update automatically as we grow.
          </p>
          <div className="ss-grid grid-4" style={{ marginTop: 14 }}>
            <KpiCard icon={<Users size={18} />} value={(tr?.totals?.users ?? 0).toLocaleString()} label="Members" />
            <KpiCard icon={<DollarSign size={18} />} value={money(tr?.totals?.gmv)} label="GMV" />
            <KpiCard icon={<ShoppingBag size={18} />} value={(tr?.totals?.shows ?? 0).toLocaleString()} label="Live shows" />
            <KpiCard icon={<HeartHandshake size={18} />} value={money(tr?.totals?.raised)} label="Raised for causes" />
          </div>
          {tr?.has_data ? (
            <TractionCharts series={tr.series} />
          ) : (
            <div className="ss-card" style={{ padding: 22, marginTop: 14, textAlign: "center", background: "var(--surface-3)" }} data-testid="traction-prelaunch">
              <div style={{ fontWeight: 800, fontSize: 16, color: "var(--brand)" }}>Invite-only launch in progress</div>
              <div className="ss-muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.6, maxWidth: 560, margin: "6px auto 0" }}>
                The product is fully built and running end-to-end. This slide is wired to live data, so member growth,
                GMV and live-show charts populate here in real time as founding members come on board.
              </div>
            </div>
          )}
          <p className="ss-muted" style={{ fontSize: 11, marginTop: 12 }}>
            Live figures{fmtDate ? ` · as of ${fmtDate}` : ""}. Product live on Web, iOS &amp; Android with real Stripe checkout, shipping &amp; email.
          </p>
        </Slide>

        {/* 8 — The Ask */}
        <Slide n={8} total={T} cur={idx}>
          <h2 className="pitch-h"><DollarSign size={22} /> The ask — $500,000</h2>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
            <span className="ss-pill" style={{ background: "var(--brand)", color: "#fff", fontWeight: 700 }} data-testid="pitch-raise">Raise: $500,000</span>
            <span className="ss-pill" style={{ background: "var(--gold)", color: "var(--brand-2)", fontWeight: 700 }} data-testid="pitch-equity">Equity: 10%</span>
            <span className="ss-pill" style={{ background: "var(--gold-soft)", color: "var(--brand)", fontWeight: 700 }} data-testid="pitch-valuation">Valuation: $4.5M pre-money · $5M post-money</span>
          </div>
          <p className="ss-muted" style={{ fontSize: 12.5, marginTop: 8 }} data-testid="pitch-captable-note">$500,000 for <b>10% equity in a priced round</b> at a $5M post-money valuation — shares are issued to investors now (not a SAFE or convertible note), so the 10% is locked in at close.</p>
          <p className="ss-muted" style={{ fontSize: 13.5, marginTop: 12, fontWeight: 700 }}>We're raising $500,000 to:</p>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, columns: 2, columnGap: 24, fontSize: 12.5, lineHeight: 1.7 }} data-testid="pitch-goals">
            {RAISE_GOALS.map((g) => <li key={g}>{g}</li>)}
          </ul>
          <p className="ss-muted" style={{ fontSize: 13, marginTop: 14, fontWeight: 700 }}>Use of funds</p>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 9 }}>
            {FUND.map((f) => (
              <div key={f.label}>
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 13.5 }}>
                  <span>{f.label}</span><span>{f.amt} · {f.pct}%</span>
                </div>
                <div style={{ height: 9, borderRadius: 999, background: "var(--surface-3)", marginTop: 4, overflow: "hidden" }}>
                  <div style={{ width: `${f.pct}%`, height: "100%", background: "var(--brand)" }} />
                </div>
              </div>
            ))}
          </div>
        </Slide>

        {/* 9 — Milestones */}
        <Slide n={9} total={T} cur={idx}>
          <h2 className="pitch-h"><TrendingUp size={22} /> Milestones by funding tranche</h2>
          <p className="ss-muted" style={{ fontSize: 13.5, marginTop: 4 }}>Capital is deployed against clear, staged targets:</p>
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }} data-testid="pitch-milestones">
            {PHASES.map((p, i) => (
              <div key={p.name} className="ss-card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8, borderTop: "3px solid var(--brand)" }} data-testid={`pitch-phase-${i + 1}`}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{p.name}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brand)" }}>{p.range}</div>
                  <div className="ss-muted" style={{ fontSize: 12.5, marginTop: 2 }}>{p.title}</div>
                </div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11.5, lineHeight: 1.6 }}>
                  {p.targets.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Slide>

        {/* 10 — Growth & monetization roadmap */}
        <Slide n={10} total={T} cur={idx}>
          <h2 className="pitch-h"><Layers size={22} /> Growth &amp; monetization roadmap</h2>
          <p className="ss-muted" style={{ fontSize: 13.5, marginTop: 4 }}>
            We build the community first, then layer in revenue as SouqStream scales by membership.
          </p>
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }} data-testid="pitch-monetization">
            {MONETIZATION.map((p, i) => (
              <div key={p.name} className="ss-card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8, borderTop: `3px solid ${i === 0 ? "var(--gold)" : "var(--brand)"}` }} data-testid={`pitch-monetize-${i + 1}`}>
                <div>
                  <span className="ss-pill" style={{ background: i === 0 ? "var(--gold-soft)" : "var(--surface-3)", color: "var(--brand)", fontSize: 10, fontWeight: 800 }}>{p.tag}</span>
                  <div style={{ fontWeight: 800, fontSize: 14, marginTop: 6 }}>{p.name}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brand)" }}>{p.range}</div>
                  <div className="ss-muted" style={{ fontSize: 12 }}>{p.title}</div>
                </div>
                <ul style={{ margin: 0, paddingLeft: 15, fontSize: 11.5, lineHeight: 1.55 }}>
                  {p.items.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <p className="ss-muted" style={{ fontSize: 11.5, marginTop: 12 }}>
            Founding Members keep their designated founding benefits while new monetization is introduced across the broader ecosystem.
          </p>
        </Slide>

        {/* 11 — Revenue projection */}
        <Slide n={11} total={T} cur={idx}>
          <h2 className="pitch-h"><Coins size={22} /> Example revenue projection</h2>
          <p className="ss-muted" style={{ fontSize: 13.5, marginTop: 4 }}>
            Illustrative model at a <b>10% effective take rate</b> on marketplace GMV, plus subscriptions &amp; advertising.
          </p>
          <div className="ss-grid grid-3" style={{ marginTop: 14 }} data-testid="pitch-revenue">
            {REVENUE.map((r, i) => (
              <div key={r.users} className="ss-card" style={{ padding: 18, display: "flex", flexDirection: "column", gap: 8, background: r.highlight ? "var(--brand)" : "var(--surface)", color: r.highlight ? "#fff" : "inherit" }} data-testid={`pitch-revenue-${i + 1}`}>
                <div style={{ fontWeight: 800, fontSize: 17 }}>{r.users}</div>
                <div style={{ fontSize: 12.5, opacity: r.highlight ? .9 : 1, color: r.highlight ? "#fff" : "var(--muted)" }}>{r.sellers}</div>
                <div style={{ fontSize: 12.5, opacity: r.highlight ? .9 : 1, color: r.highlight ? "#fff" : "var(--muted)" }}>{r.gmv}</div>
                <div style={{ height: 1, background: r.highlight ? "rgba(255,255,255,.2)" : "var(--line)", margin: "2px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><span style={{ opacity: .85 }}>Take rate (10%)</span><b>{r.take}</b></div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}><span style={{ opacity: .85 }}>Subs &amp; ads</span><b>{r.other}</b></div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, marginTop: 2, color: r.highlight ? "var(--gold)" : "var(--brand)" }}><span>Total</span><span>{r.total}</span></div>
              </div>
            ))}
          </div>
          <p className="ss-muted" style={{ fontSize: 11, marginTop: 12 }}>
            Illustrative projections for modeling purposes only; actual results depend on adoption, seller activity &amp; monetization mix.
          </p>
        </Slide>

        {/* 12 — Ecosystem flywheel */}
        <Slide n={12} total={T} cur={idx}>
          <h2 className="pitch-h"><Repeat size={22} /> The ecosystem flywheel</h2>
          <div className="ss-card" style={{ padding: 20, marginTop: 8, background: "var(--brand)", color: "#fff" }}>
            <div style={{ fontSize: 19, fontWeight: 800, lineHeight: 1.3 }}>
              Our 1,500 Founding Members create the network. Our marketplace monetizes the ecosystem.
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }} data-testid="pitch-flywheel">
            {FLYWHEEL.map((step, i) => (
              <React.Fragment key={step}>
                <span className="ss-pill" style={{
                  background: i === 0 ? "var(--gold)" : i === FLYWHEEL.length - 1 ? "var(--brand)" : "var(--gold-soft)",
                  color: i === 0 ? "var(--brand-2)" : i === FLYWHEEL.length - 1 ? "#fff" : "var(--brand)",
                  fontWeight: 800, fontSize: 12.5, padding: "7px 13px",
                }}>{step}</span>
                {i < FLYWHEEL.length - 1 && <ChevronRight size={16} style={{ color: "var(--muted)" }} />}
              </React.Fragment>
            ))}
          </div>
          <p className="ss-muted" style={{ fontSize: 12.5, marginTop: 18, lineHeight: 1.6 }}>
            Our first 1,500 users receive <b>protected Founding Member benefits</b>, with the specific benefits and pricing
            protections defined at enrollment. New monetization is introduced across the broader SouqStream ecosystem
            without disrupting those founding protections.
          </p>
        </Slide>

        {/* 13 — Founder + close */}
        <Slide n={13} total={T} cur={idx}>
          <h2 className="pitch-h"><HandHeart size={22} /> Founder &amp; vision</h2>
          <div className="ss-card" style={{ padding: 22, marginTop: 8, display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 84, height: 84, borderRadius: 999, overflow: "hidden", border: "3px solid var(--gold)", flexShrink: 0 }}>
              <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_desktop-streaming-1/artifacts/ztnsizxf_file_00000000df2471fdafb86646f0303b7c.png" alt="Jafar Rizvi" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontWeight: 800, fontSize: 18 }}>Jafar Rizvi — Founder &amp; CEO</div>
              <div className="ss-muted" style={{ fontSize: 14, lineHeight: 1.7, marginTop: 4 }}>
                Jafar founded Souq Stream to combine live streaming, e-commerce, community and charitable giving in one
                place — building a trusted home where Muslim sellers and shoppers connect, and where commerce strengthens the community.
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 26 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "var(--brand)" }}>Souq Stream — Where Live Shopping, Community &amp; Giving Come Together.</div>
            <div className="ss-muted" style={{ marginTop: 8 }}>Let's build the marketplace this community deserves.</div>
          </div>
        </Slide>
        </div>

        <button className="pitch-arrow no-print" onClick={next} disabled={idx === T - 1} aria-label="Next slide" data-testid="pitch-next"><ChevronRight size={22} /></button>
      </div>

      <div className="pitch-nav no-print" data-testid="pitch-nav">
        <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={prev} disabled={idx === 0} data-testid="pitch-nav-prev"><ChevronLeft size={15} /> Prev</button>
        <div className="pitch-dots">
          {Array.from({ length: T }).map((_, i) => (
            <button
              key={i}
              className={`pitch-dot${i === idx ? " active" : ""}`}
              onClick={() => go(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`pitch-dot-${i + 1}`}
            />
          ))}
        </div>
        <div className="pitch-counter" data-testid="pitch-counter">{idx + 1} / {T}</div>
        <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={next} disabled={idx === T - 1} data-testid="pitch-nav-next">Next <ChevronRight size={15} /></button>
      </div>
    </div>
  );
}
